import React, { useState, useEffect } from 'react';
import Navbar from './components/Navbar.tsx';
import Hero from './components/Hero.tsx';
import Features from './components/Features.tsx';
import Portfolio from './components/Portfolio.tsx';
import AIConsultant from './components/AIConsultant.tsx';
import Pricing from './components/Pricing.tsx';
import Footer from './components/Footer.tsx';
import AdminPortal from './components/AdminPortal.tsx';
import PrivacyPolicy from './components/PrivacyPolicy.tsx';
import WhatsAppWidget from './components/WhatsAppWidget.tsx';
import { Loader2, ZapOff, Sparkles, ArrowUp } from 'lucide-react';
import { db, auth } from './services/firebase.ts';
import { collection, addDoc } from 'firebase/firestore';
import { signInAnonymously } from 'firebase/auth';

const App: React.FC = () => {
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubmittingNews, setIsSubmittingNews] = useState(false);
  const [newsSuccess, setNewsSuccess] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const ensureUser = async () => {
    if (!auth) throw new Error("Firebase Auth Offline");
    if (!auth.currentUser) await signInAnonymously(auth);
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsletterEmail) return;

    if (!db || !auth) {
      alert("System is currently offline (Demo Mode).");
      return;
    }

    setIsSubmittingNews(true);
    try {
      await ensureUser();

      // Following Section 5.1: apps/{appId}/records
      await addDoc(collection(db, 'apps/tiny-systems/newsletter'), {
        email: newsletterEmail,
        date: new Date().toISOString()
      });
      
      setIsSubmittingNews(false);
      setNewsSuccess(true);
      setNewsletterEmail('');
      setTimeout(() => setNewsSuccess(false), 8000);
    } catch (err) {
      console.error("Newsletter Save Error:", err);
      setIsSubmittingNews(false);
      alert("Failed to subscribe. Please try again later.");
    }
  };

  if (showPrivacyPolicy) {
    return <PrivacyPolicy onBack={() => setShowPrivacyPolicy(false)} />;
  }

  return (
    <div className="min-h-screen selection:bg-[#7FAF9A]/30 relative">
      <Navbar />
      <main>
        <Hero />
        <Features />
        <Portfolio />
        <AIConsultant />
        <Pricing />
        
        <section className="py-24 md:py-40 bg-white px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1F2933]/5 text-[#1F2933]/40 text-[10px] font-black uppercase tracking-[0.2em] mb-8 md:mb-12">
              <ZapOff size={14} />
              <span>Ditch the App Fatigue</span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-7xl font-extrabold text-[#1F2933] mb-6 md:mb-10 tracking-tight leading-[1.05]">
              Build Systems, <br/><span className="text-[#7FAF9A]">Not Downloads.</span>
            </h2>
            <p className="text-lg md:text-xl lg:text-2xl text-[#1F2933]/60 mb-10 md:mb-16 max-w-2xl mx-auto leading-relaxed font-medium">
              Enough with the mobile apps—people are tired of installing things they barely use. 
              Want to learn how to build friction-free web tools that maximize your business without an app store?
            </p>
            
            {newsSuccess ? (
              <div className="max-w-lg mx-auto bg-[#7FAF9A]/10 p-8 md:p-10 rounded-[2rem] md:rounded-[2.5rem] animate-in zoom-in-95 duration-500 border border-[#7FAF9A]/20">
                <div className="flex justify-center mb-4">
                   <div className="bg-[#7FAF9A] p-3 rounded-full text-white">
                     <Sparkles size={24} />
                   </div>
                </div>
                <h3 className="text-xl md:text-2xl font-black text-[#7FAF9A] mb-2">Welcome to the Studio!</h3>
                <p className="text-[#1F2933]/60 font-bold text-sm md:text-base">Your "Calm Growth" guide is being prepared and will arrive in your inbox shortly.</p>
              </div>
            ) : (
              <form className="flex flex-col sm:flex-row gap-4 md:gap-5 max-w-xl mx-auto" onSubmit={handleNewsletterSubmit}>
                <input 
                  type="email" 
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  placeholder="Your email." 
                  className="flex-1 px-6 md:px-8 py-5 md:py-6 rounded-2xl md:rounded-[1.5rem] border-2 border-[#1F2933]/5 focus:border-[#7FAF9A] outline-none bg-[#F4F1EC]/30 font-bold text-lg transition-all"
                  required
                  disabled={isSubmittingNews}
                />
                <button 
                  type="submit" 
                  disabled={isSubmittingNews}
                  className="btn-sage px-8 md:px-12 py-5 md:py-6 rounded-2xl md:rounded-[1.5rem] font-black text-lg md:text-xl shadow-xl md:shadow-2xl shadow-sage-900/20 flex items-center justify-center min-w-[180px] hover:scale-105 transition-transform disabled:opacity-50"
                >
                  {isSubmittingNews ? <Loader2 className="animate-spin" size={24} /> : 'Send the guide'}
                </button>
              </form>
            )}
            
            <p className="mt-10 md:mt-12 text-[9px] md:text-xs text-[#1F2933]/30 font-black uppercase tracking-[0.3em]">
              Instant Knowledge. No App Store Approval Required.
            </p>
          </div>
        </section>
      </main>
      
      <Footer 
        onAdminRequest={() => setIsAdminOpen(true)} 
        onPrivacyRequest={() => setShowPrivacyPolicy(true)} 
      />

      {/* Back to Top - Bottom Left */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className={`fixed bottom-6 left-6 z-40 bg-[#1F2933] text-white p-3 rounded-2xl shadow-xl border border-white/10 transition-all duration-300 hover:scale-110 hover:bg-black ${
          showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'
        }`}
      >
        <ArrowUp size={24} />
      </button>

      {isAdminOpen && <AdminPortal onClose={() => setIsAdminOpen(false)} />}

      <WhatsAppWidget />
    </div>
  );
};

export default App;